import getWavHeaders from 'wav-headers';
import {
  TARGET_SAMPLE_RATE,
  TARGET_CHANNELS,
  TARGET_BIT_DEPTH,
} from '@src/constants/audio';

/**
 * Convert Float32Array audio buffer to 16-bit PCM
 */
const floatTo16BitPCM = (output, startOffset, input) => {
  let offset = startOffset;
  for (let i = 0; i < input.length; i += 1, offset += 2) {
    const s = Math.max(-1, Math.min(1, input[i]));
    output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
};

/**
 * Downsample audio buffer to target sample rate
 */
const downSampleBuffer = (buffer, originalSampleRate, exportSampleRate) => {
  if (exportSampleRate === originalSampleRate) {
    return buffer;
  }

  const sampleRateRatio = originalSampleRate / exportSampleRate;
  const newLength = Math.round(buffer.length / sampleRateRatio);
  const result = new Float32Array(newLength);

  let offsetResult = 0;
  let offsetBuffer = 0;

  while (offsetResult < result.length) {
    const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
    let accum = 0;
    let count = 0;
    for (
      let i = offsetBuffer;
      i < nextOffsetBuffer && i < buffer.length;
      i += 1
    ) {
      accum += buffer[i];
      count += 1;
    }
    result[offsetResult] = accum / count;
    offsetResult += 1;
    offsetBuffer = nextOffsetBuffer;
  }
  return result;
};

/**
 * Convert stereo to mono
 */
const convertToMono = (audioBuffer) => {
  if (audioBuffer.numberOfChannels === 1) {
    return audioBuffer.getChannelData(0);
  }

  const leftChannel = audioBuffer.getChannelData(0);
  const rightChannel = audioBuffer.getChannelData(1);
  const monoData = new Float32Array(leftChannel.length);

  for (let i = 0; i < leftChannel.length; i += 1) {
    monoData[i] = (leftChannel[i] + rightChannel[i]) / 2;
  }

  return monoData;
};

/**
 * Convert audio blob to WAV format with specified parameters
 * @param {Blob} audioBlob - The audio blob to convert
 * @param {Object} options - Conversion options
 * @param {number} options.sampleRate - Target sample rate (default: 48000)
 * @param {number} options.channels - Target number of channels (default: 1)
 * @param {number} options.bitDepth - Target bit depth (default: 16)
 * @returns {Promise<Blob>} WAV formatted blob
 */
const convertAudioToWav = async (
  audioBlob,
  options = {
    sampleRate: TARGET_SAMPLE_RATE,
    channels: TARGET_CHANNELS,
    bitDepth: TARGET_BIT_DEPTH,
  },
) => {
  try {
    const { sampleRate, channels, bitDepth } = options;

    // Create AudioContext
    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();
    const arrayBuffer = await audioBlob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    // Convert to mono
    const monoData = convertToMono(audioBuffer);

    // Resample to target sample rate
    const resampledData = downSampleBuffer(
      monoData,
      audioBuffer.sampleRate,
      sampleRate,
    );

    // Convert Float32Array to 16-bit PCM
    const pcmData = new Int16Array(resampledData.length);
    const dataView = new DataView(pcmData.buffer);
    floatTo16BitPCM(dataView, 0, resampledData);

    // Create WAV header
    const wavHeader = getWavHeaders({
      channels,
      sampleRate,
      bitDepth,
    });

    // Convert PCM data to Buffer
    const pcmBuffer = Buffer.from(pcmData.buffer);

    // Combine header and PCM data
    const encodedWav = Buffer.concat([wavHeader, pcmBuffer]);

    // Convert Buffer to ArrayBuffer for Blob
    const wavArrayBuffer = new ArrayBuffer(encodedWav.length);
    const view = new Uint8Array(wavArrayBuffer);
    for (let i = 0; i < encodedWav.length; i += 1) {
      view[i] = encodedWav[i];
    }

    // Create Blob from WAV data
    const wavBlob = new Blob([wavArrayBuffer], { type: 'audio/wav' });

    return wavBlob;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error converting audio to WAV:', error);
    throw error;
  }
};

const decodeWAV = (rawWav) => {
  if (typeof rawWav === 'string') {
    // eslint-disable-next-line no-param-reassign
    rawWav = Buffer.from(rawWav, 'binary');
  }
  if (!Buffer.isBuffer(rawWav)) {
    throw new TypeError('pcm data must be Buffer or string');
  }
  // eslint-disable-next-line no-param-reassign
  rawWav = rawWav.subarray(44);
  return rawWav;
};

const toArrayBuffer = (buffer) => {
  const arrayBuffer = new ArrayBuffer(buffer.length);
  const view = new Uint8Array(arrayBuffer);
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < buffer.length; ++i) {
    view[i] = buffer[i];
  }
  return arrayBuffer;
};

const toBuffer = (arrayBuffer) => {
  const buffer = Buffer.alloc(arrayBuffer.byteLength);
  const view = new Uint8Array(arrayBuffer);
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < buffer.length; ++i) {
    buffer[i] = view[i];
  }
  return buffer;
};

const exportAudioBufferToAudio = async (audioBuffer) => {
  const wavHeader = getWavHeaders({
    channels: 1,
    sampleRate: 48000,
    bitDepth: 16,
  });
  const encodedWav = Buffer.concat([wavHeader, audioBuffer]);
  const blob = new Blob([toArrayBuffer(encodedWav)], { type: 'audio/wav' });
  return blob;
};

const getAudioDuration = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = new window.Audio();
    audio.src = url;
    audio.addEventListener('loadedmetadata', () => {
      const { duration } = audio;

      const durationRounded = Math.floor(duration);
      resolve(durationRounded);
      URL.revokeObjectURL(url);
    });
    audio.addEventListener('error', () => {
      resolve(0);
      URL.revokeObjectURL(url);
    });
  });

// Resolve the duration (seconds, float — NOT floored, unlike getAudioDuration)
// of an already-hosted audio URL. Used to realign a dubbing block's timeline
// slot to its synthesized audio. Resolves 0 on error/missing so callers skip
// gracefully.
const loadAudioDurationFromUrl = (url) =>
  new Promise((resolve) => {
    if (!url) {
      resolve(0);
      return;
    }
    const audio = new window.Audio();
    audio.preload = 'metadata';
    const onLoaded = () => {
      // eslint-disable-next-line no-use-before-define
      cleanup();
      resolve(audio.duration || 0);
    };
    const onError = () => {
      // eslint-disable-next-line no-use-before-define
      cleanup();
      resolve(0);
    };
    function cleanup() {
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('error', onError);
    }
    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('error', onError);
    audio.src = url;
  });

const formatDuration = (durationSeconds) => {
  if (!durationSeconds && durationSeconds !== 0) return '';
  if (typeof durationSeconds === 'string') return durationSeconds;

  const totalSeconds = Math.floor(durationSeconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  return `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;
};

export {
  decodeWAV,
  toArrayBuffer,
  toBuffer,
  exportAudioBufferToAudio,
  getAudioDuration,
  loadAudioDurationFromUrl,
  formatDuration,
  convertAudioToWav,
  floatTo16BitPCM,
  downSampleBuffer,
  convertToMono,
  TARGET_SAMPLE_RATE,
  TARGET_CHANNELS,
  TARGET_BIT_DEPTH,
};
